import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ProjectDisplay from '../components/ProjectDisplay';
import TechBackground from '../components/TechBackground';

const ProjectsPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [animateCards, setAnimateCards] = useState(false);

  const [projectsRef, projectsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Sample project data - you would typically fetch this from an API or CMS
  const projects = [
    {
      id: 1,
      title: "AI-Powered E-commerce Platform",
      description: "A fully responsive e-commerce platform with AI-driven product recommendations and personalized shopping experiences.",
      category: "e-commerce",
      image: "https://via.placeholder.com/600x400/0f172a/38bdf8?text=E-commerce+Platform",
      type: "ecommerce-project",
      features: ["AI Recommendations", "User Analytics", "Mobile Responsive"],
      techStack: ["React", "Node.js", "TensorFlow", "MongoDB"],
      link: "/projects/ai-ecommerce"
    },
    {
      id: 2,
      title: "Business Intelligence Dashboard",
      description: "Real-time analytics dashboard providing actionable insights for business decision makers with interactive data visualization.",
      category: "dashboard",
      image: "https://via.placeholder.com/600x400/0f172a/38bdf8?text=BI+Dashboard",
      type: "dashboard-project",
      features: ["Real-time Analytics", "Interactive Charts", "Data Export"],
      techStack: ["Vue.js", "D3.js", "Firebase", "Express"],
      link: "/projects/bi-dashboard"
    },
    {
      id: 3,
      title: "SaaS Marketing Platform",
      description: "All-in-one marketing automation platform for SaaS companies to manage campaigns, track performance, and grow their audience.",
      category: "saas",
      image: "https://via.placeholder.com/600x400/0f172a/38bdf8?text=SaaS+Marketing",
      type: "saas-project",
      features: ["Email Automation", "A/B Testing", "Analytics Suite"],
      techStack: ["React", "Redux", "Node.js", "PostgreSQL"],
      link: "/projects/saas-marketing"
    },
    {
      id: 4,
      title: "Healthcare Patient Portal",
      description: "Secure patient portal allowing users to schedule appointments, view medical records, and communicate with healthcare providers.",
      category: "healthcare",
      image: "https://via.placeholder.com/600x400/0f172a/38bdf8?text=Healthcare+Portal",
      type: "healthcare-project",
      features: ["Secure Messaging", "Appointment Scheduling", "Medical Records"],
      techStack: ["Angular", "Express", "MongoDB", "Socket.io"],
      link: "/projects/healthcare-portal"
    },
    {
      id: 5,
      title: "Real Estate Virtual Tours",
      description: "Interactive 3D virtual tour platform for real estate listings with VR support and detailed property analytics.",
      category: "real-estate",
      image: "https://via.placeholder.com/600x400/0f172a/38bdf8?text=Virtual+Tours",
      type: "vr-project",
      features: ["VR Support", "3D Modeling", "Property Analytics"],
      techStack: ["Three.js", "React", "WebXR", "Node.js"],
      link: "/projects/virtual-tours"
    },
    {
      id: 6,
      title: "Fintech Investment Platform",
      description: "Secure investment platform with real-time market data, portfolio management, and AI-powered investment recommendations.",
      category: "fintech",
      image: "https://via.placeholder.com/600x400/0f172a/38bdf8?text=Fintech+Platform",
      type: "financial-project",
      features: ["Portfolio Management", "Market Analysis", "Secure Transactions"],
      techStack: ["React", "Node.js", "Python", "PostgreSQL"],
      link: "/projects/fintech-platform"
    }
  ];

  // Filter options for the projects
  const filterOptions = [
    { value: 'all', label: 'All Projects' },
    { value: 'e-commerce', label: 'E-Commerce' },
    { value: 'dashboard', label: 'Dashboards' },
    { value: 'saas', label: 'SaaS' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'fintech', label: 'Fintech' }
  ];

  // Apply filtering when activeFilter changes
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => project.category === activeFilter);
      setFilteredProjects(filtered);
    }
    
    // Small delay to trigger animation after filter change
    setTimeout(() => setAnimateCards(true), 100);
    
    return () => {
      setAnimateCards(false);
    };
  }, [activeFilter]);

  // Initial data load
  useEffect(() => {
    setFilteredProjects(projects);
  }, []);

  // Animation variants for container and items
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="projects-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section - Updated to match ServicesPage style */}
      <motion.section 
        className="services-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-background">
          <TechBackground />
        </div>
        <div className="hero-content">
          <motion.h1
            className="gradient-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Our Projects Portfolio
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Explore our innovative solutions across various industries and technologies
          </motion.p>
          <motion.div 
            className="hero-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <motion.button
              className="cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Project
              <div className="button-glow"></div>
            </motion.button>
            <motion.button
              className="outline-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Case Studies
            </motion.button>
          </motion.div>
        </div>
      </motion.section>

      {/* Main Content */}
      <div className="projects-grid-container">
        {/* Filter Options */}
        <div className="project-filters" ref={projectsRef}>
          <div className="filter-container glass-morphism">
            {filterOptions.map(option => (
              <button
                key={option.value}
                className={`filter-button ${activeFilter === option.value ? 'active' : ''}`}
                onClick={() => setActiveFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Project Grid */}
        <motion.div
          className="projects-grid"
          variants={containerVariants}
          initial="hidden"
          animate={projectsInView && animateCards ? "visible" : "hidden"}
        >
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              className={`project-card ${project.type} ${index === 0 || index === 3 ? 'large' : ''}`}
              variants={itemVariants}
            >
              <ProjectDisplay 
                title={project.title}
                description={project.description}
                image={project.image}
                type={project.type}
                features={project.features}
                techStack={project.techStack}
                link={project.link}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* No Results Message */}
        {filteredProjects.length === 0 && (
          <motion.div 
            className="no-results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <h3>No projects found</h3>
            <p>Try selecting a different filter category</p>
          </motion.div>
        )}

        {/* Call to Action */}
        <div className="projects-cta glass-morphism">
          <h2>Have a project in mind?</h2>
          <p>Let's discuss how we can help bring your vision to life with our expertise in cutting-edge technologies.</p>
          <a href="/contact" className="cta-button">
            <span>Start a Project</span>
            <div className="button-glow"></div>
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectsPage;